import BodyText from '@lyra/core/components/BodyText'
import Spinner from '@lyra/core/components/Spinner'
import Table, { createColumnHelper, TableData } from '@lyra/core/components/Table'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatTruncatedUSD from '@lyra/core/utils/formatTruncatedUSD'
import MarketIcon from '@lyra/web/components/common/MarketIcon'
import { InstrumentType } from '@lyra/web/constants/instruments'
import { SECTION_SPINNER_SIZE } from '@lyra/web/constants/layout'
import { CurrencyId } from '@lyra/web/constants/tokens'
import useAllMarketStats from '@lyra/web/hooks/useAllMarketStats'
import useCurrencies from '@lyra/web/hooks/useCurrencies'
import { formatMarketSymbol } from '@lyra/web/utils/markets'
import {
  getListedOptionsMarketIds,
  getListedPerpsMarketIds,
  getListedSpotCollateralIds,
} from '@lyra/web/utils/markets'
import { useMemo } from 'react'
import { StackProps, XStack } from 'tamagui'

type Props = {
  instrumentType?: InstrumentType
  onPressMarket?: (currencyId: CurrencyId, instrumentType: InstrumentType) => void
} & StackProps

type MarketRowData = TableData<{
  currencyId: CurrencyId
  instrumentType: InstrumentType
  spotPrice: number
  spotPrice24hPctChange: number
  volume24h: number
  openInterest: number
}>

export default function MarketsTable({ instrumentType, onPressMarket, ...stackProps }: Props) {
  const currencies = useCurrencies()
  const { data: stats, isLoading, error } = useAllMarketStats()

  const activeCurrenciesList: { currencyId: CurrencyId; instrumentType: InstrumentType }[] =
    useMemo(() => {
      const currenciesList = (
        getListedOptionsMarketIds()
          .map((currencyId) => ({
            currencyId,
            instrumentType: InstrumentType.Options,
          }))
          .concat(
            getListedPerpsMarketIds().map((currencyId) => ({
              currencyId,
              instrumentType: InstrumentType.Perps,
            }))
          ) as { currencyId: CurrencyId; instrumentType: InstrumentType }[]
      ).concat(
        getListedSpotCollateralIds().map((currencyId) => ({
          currencyId,
          instrumentType: InstrumentType.Spot,
        }))
      )

      if (instrumentType) {
        return currenciesList.filter((c) => c.instrumentType === instrumentType)
      } else {
        return currenciesList
      }
    }, [instrumentType])

  const data: MarketRowData[] = useMemo(() => {
    return activeCurrenciesList
      .map(({ currencyId, instrumentType }) => {
        const marketStats = stats ? stats[instrumentType][currencyId] : null
        return {
          currencyId,
          instrumentType,
          spotPrice: currencies[currencyId].spotPrice,
          spotPrice24hPctChange: currencies[currencyId].spotPrice24hPctChange,
          volume24h: marketStats ? +marketStats.daily_notional_volume : 0,
          openInterest: marketStats ? +marketStats.open_interest : 0,
          instrumentTypePriority:
            instrumentType === InstrumentType.Options
              ? 0
              : instrumentType === InstrumentType.Perps
              ? 1
              : instrumentType === InstrumentType.Spot
              ? 2
              : 3,
          onPress: onPressMarket ? () => onPressMarket(currencyId, instrumentType) : undefined,
        }
      })
      .sort((a, b) => {
        if (a.instrumentTypePriority !== b.instrumentTypePriority) {
          return a.instrumentTypePriority - b.instrumentTypePriority
        }
        return b.volume24h - a.volume24h
      })
  }, [activeCurrenciesList, currencies, stats, onPressMarket])

  const columnHelper = createColumnHelper<MarketRowData>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('market', {
        header: 'Market',
        meta: {
          minWidth: 120,
        },
        cell: (props) => {
          const { currencyId, instrumentType } = props.row.original
          return (
            <XStack gap="$1" alignItems="center">
              <MarketIcon marketId={currencyId} size={20} />
              <BodyText>{formatMarketSymbol(currencyId, instrumentType)}</BodyText>
            </XStack>
          )
        },
      }),
      columnHelper.accessor('price', {
        header: 'Price',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          const { spotPrice } = props.row.original
          return <BodyText>{`$${formatNumber(spotPrice, { minDps: 2, maxDps: 18 })}`}</BodyText>
        },
      }),
      columnHelper.accessor('24hChange', {
        header: '24h Change',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          const { spotPrice24hPctChange } = props.row.original
          return (
            <BodyText color={spotPrice24hPctChange >= 0 ? 'green' : 'red'}>
              {formatPercentage(spotPrice24hPctChange, { showSign: true })}
            </BodyText>
          )
        },
      }),
      columnHelper.accessor('24hVolume', {
        header: '24h Volume',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          const { volume24h } = props.row.original
          return <BodyText>{formatTruncatedUSD(volume24h)}</BodyText>
        },
      }),
      columnHelper.accessor('openInterest', {
        header: 'Open Interest',
        meta: {
          minWidth: 100,
        },
        cell: (props) => {
          const { openInterest, spotPrice, instrumentType } = props.row.original
          return (
            <BodyText>
              {instrumentType === InstrumentType.Spot
                ? '—'
                : formatTruncatedUSD(openInterest * spotPrice)}
            </BodyText>
          )
        },
      }),
    ],
    [columnHelper]
  )

  if (isLoading || error) {
    return (
      <XStack {...stackProps} justifyContent="center" alignItems="center">
        {error ? (
          <BodyText color="secondary">Failed to load markets</BodyText>
        ) : (
          <Spinner color="red" size={SECTION_SPINNER_SIZE} />
        )}
      </XStack>
    )
  }

  return <Table {...stackProps} columns={columns} data={data} emptyMessage="No markets" />
}
